/**
 * Orella Health — src/lib/transcribeStream.js
 * Session D · step 4.3 · AI-2 + AI-4
 *
 * Client-side Amazon Transcribe streaming over a presigned WebSocket.
 * Audio goes browser -> AWS directly. It never transits Orella compute.
 *
 * ── TWIN FILE ────────────────────────────────────────────────────────────
 * This file is DUPLICATED, byte-for-byte, into:
 *     orella-patient-app/src/lib/transcribeStream.js
 *     orella-md-panel/src/lib/transcribeStream.js
 * There is no shared package (matches the existing 4-repo pattern).
 * If you edit one, edit the other in the same commit or they drift silently.
 * The module deliberately imports NOTHING — no React, no api.js — so the two
 * copies stay identical even though the repos' API clients differ.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Contract fixed by api/transcribe_stream.py (v243.41.0):
 *   media-encoding = pcm   |   sample-rate = 16000   |   mono
 * These are SIGNED INTO THE URL and cannot be renegotiated by the client.
 *
 * Usage:
 *   import { startTranscribeStream } from '../lib/transcribeStream'
 *
 *   const session = await startTranscribeStream({
 *     mint: () => transcriptionAPI.streamUrl({ surface: 'patient', language: 'en' }),
 *     onReady:   (info) => console.log('ready', info),
 *     onPartial: (text) => setPreview(text),
 *     onCommit:  (text) => appendCommitted(text),
 *     onError:   (err)  => console.error(err),
 *     onClose:   (info) => console.log('closed', info),
 *   })
 *   ...
 *   await session.stop()
 */

const TARGET_SAMPLE_RATE = 16000;
const FRAME_SAMPLES = 1600; // 100 ms @ 16 kHz -> 3200 bytes per audio event

// How long stop() waits, after the end-of-stream marker, for Transcribe to
// deliver its final result and close the socket.
//
// THE DEFECT THIS NUMBER REPLACES (TF b40, FU-STREAM-DRAIN-1).
// Transcribe emits a non-partial result at a natural pause, and emits whatever
// is left as ONE final result only after it sees the zero-length AudioEvent.
// A patient who answers "how have you been?" in a single 20-second breath
// produces NO mid-stream commit at all: every word of it is in that last
// flush. stop() used to send the marker, schedule ws.close() 800 ms later on a
// bare setTimeout, and RESOLVE IMMEDIATELY — so `await session.stop()` returned
// in about a millisecond and the caller read its committed-text ref while the
// text was still on the wire. It arrived ~700 ms later and appended to a ref
// nobody read again.
//
// DescribeVoice had papered over this at ITS call site with a fixed
// `await new Promise(r => setTimeout(r, 900))` (v242.53.0, comment: "close the
// stream FIRST so the trailing partial flushes"). FollowUpReportModal,
// useVoiceInput and the MD panel's useConsultRecording never got that line, so
// Describe transcribed and the follow-up report did not. The compensation
// belonged in stop(), not in one of five call sites — a drain the caller has to
// remember is a drain four callers forget.
//
// This is a CEILING, not a delay: Transcribe closes the stream itself once the
// final result is delivered, and the wait ends on that close. It is generous
// because the case that failed was on cellular, where 800 ms could truncate the
// flush even when someone did remember to wait for it.
const FLUSH_TIMEOUT_MS = 4000;

// ═══════════════════════════════════════════════════════════════════════════
// 1 · CRC32 (IEEE 802.3) — required by the AWS event-stream framing
// ═══════════════════════════════════════════════════════════════════════════

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes, start, end) {
  let c = 0xffffffff;
  for (let i = start; i < end; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// 2 · AWS event-stream framing
//
//   [total length   : 4 BE]
//   [headers length : 4 BE]
//   [prelude CRC32  : 4 BE]   <- CRC of the first 8 bytes
//   [headers        : variable]
//   [payload        : variable]
//   [message CRC32  : 4 BE]   <- CRC of everything before it
//
// Header: [name len:1][name][value type:1][value len:2 BE][value]
//         value type 0x07 = UTF-8 string
// ═══════════════════════════════════════════════════════════════════════════

function encodeHeaders(pairs) {
  const parts = [];
  let len = 0;
  for (const [name, value] of pairs) {
    const n = new TextEncoder().encode(name);
    const v = new TextEncoder().encode(value);
    const h = new Uint8Array(1 + n.length + 1 + 2 + v.length);
    const dv = new DataView(h.buffer);
    let o = 0;
    h[o++] = n.length;
    h.set(n, o); o += n.length;
    h[o++] = 0x07;
    dv.setUint16(o, v.length, false); o += 2;
    h.set(v, o);
    parts.push(h);
    len += h.length;
  }
  const out = new Uint8Array(len);
  let off = 0;
  for (const p of parts) { out.set(p, off); off += p.length; }
  return out;
}

const AUDIO_EVENT_HEADERS = encodeHeaders([
  [':message-type', 'event'],
  [':event-type', 'AudioEvent'],
  [':content-type', 'application/octet-stream'],
]);

function buildAudioEvent(payload) {
  const headers = AUDIO_EVENT_HEADERS;
  const total = 16 + headers.length + payload.length;
  const buf = new ArrayBuffer(total);
  const bytes = new Uint8Array(buf);
  const view = new DataView(buf);

  view.setUint32(0, total, false);
  view.setUint32(4, headers.length, false);
  view.setUint32(8, crc32(bytes, 0, 8), false);
  bytes.set(headers, 12);
  bytes.set(payload, 12 + headers.length);
  view.setUint32(total - 4, crc32(bytes, 0, total - 4), false);

  return buf;
}

// value-type -> fixed byte length; 6 (bytearray) and 7 (string) are 2-byte
// length-prefixed and handled separately.
const HEADER_VALUE_SIZE = { 0: 0, 1: 0, 2: 1, 3: 2, 4: 4, 5: 8, 8: 8, 9: 16 };

function decodeMessage(buf) {
  const bytes = new Uint8Array(buf);
  const view = new DataView(buf);
  const total = view.getUint32(0, false);
  const headersLen = view.getUint32(4, false);

  const headers = {};
  let off = 12;
  const headersEnd = 12 + headersLen;
  const dec = new TextDecoder();

  while (off < headersEnd) {
    const nameLen = bytes[off]; off += 1;
    const name = dec.decode(bytes.subarray(off, off + nameLen)); off += nameLen;
    const type = bytes[off]; off += 1;
    if (type === 0x07 || type === 0x06) {
      const vlen = view.getUint16(off, false); off += 2;
      headers[name] = dec.decode(bytes.subarray(off, off + vlen));
      off += vlen;
    } else {
      off += HEADER_VALUE_SIZE[type] ?? 0;
    }
  }

  const payload = bytes.subarray(headersEnd, total - 4);
  return { headers, payload: payload.length ? dec.decode(payload) : '' };
}

// ═══════════════════════════════════════════════════════════════════════════
// 3 · PCM worklet
//
// Loaded from a Blob URL rather than an emitted asset. Under Capacitor the
// app runs from capacitor://localhost, and a hashed /assets/*.js worklet URL
// is the untested path; a blob inherits the document origin and needs no
// vite config. Same code path on web and iOS.
// ═══════════════════════════════════════════════════════════════════════════

const WORKLET_SRC = `
class OrellaPCMProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const o = (options && options.processorOptions) || {};
    this.targetRate = o.targetRate || ${TARGET_SAMPLE_RATE};
    this.frameSamples = o.frameSamples || ${FRAME_SAMPLES};
    this.ratio = sampleRate / this.targetRate;
    this.buf = new Int16Array(this.frameSamples);
    this.n = 0;
    this.pos = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch || ch.length === 0) return true;
    let p = this.pos;
    while (p < ch.length) {
      const i = Math.floor(p);
      const frac = p - i;
      const s0 = i < ch.length ? ch[i] : ch[ch.length - 1];
      const s1 = i + 1 < ch.length ? ch[i + 1] : ch[ch.length - 1];
      let s = s0 + (s1 - s0) * frac;
      if (s > 1) s = 1; else if (s < -1) s = -1;
      this.buf[this.n++] = s < 0 ? s * 0x8000 : s * 0x7fff;
      if (this.n === this.frameSamples) {
        const out = new Int16Array(this.buf);
        this.port.postMessage(out.buffer, [out.buffer]);
        this.n = 0;
      }
      p += this.ratio;
    }
    this.pos = p - ch.length;
    return true;
  }
}
registerProcessor('orella-pcm', OrellaPCMProcessor);
`;

function floatToPCM16(float32, ratio, state) {
  // ScriptProcessorNode fallback path — same resample, on the main thread.
  const out = [];
  let p = state.pos;
  while (p < float32.length) {
    const i = Math.floor(p);
    const frac = p - i;
    const s0 = float32[i];
    const s1 = i + 1 < float32.length ? float32[i + 1] : float32[float32.length - 1];
    let s = s0 + (s1 - s0) * frac;
    if (s > 1) s = 1; else if (s < -1) s = -1;
    out.push(s < 0 ? s * 0x8000 : s * 0x7fff);
    p += ratio;
  }
  state.pos = p - float32.length;
  return Int16Array.from(out);
}

// ═══════════════════════════════════════════════════════════════════════════
// 4 · Public API
// ═══════════════════════════════════════════════════════════════════════════

/**
 * @param {object}   opts
 * @param {Function} opts.mint       () => Promise<{url, sampleRate, languageCode, ...}>
 *                                   Injected so this file has no repo-specific import.
 *                                   MUST be called on mic press, not page load — the
 *                                   presigned URL expires 300 s after minting. The
 *                                   expiry gates the handshake only; an opened stream
 *                                   persists (proven 2026-07-23).
 * @param {Function} [opts.onReady]  (info) => void — fires once the socket is open
 * @param {Function} [opts.onPartial](text, result) => void — revises in place
 * @param {Function} [opts.onCommit] (text, result) => void — immutable
 * @param {Function} [opts.onError]  (err) => void
 * @param {Function} [opts.onClose]  ({code, reason, clean}) => void
 * @returns {Promise<{stop: Function, info: object}>}
 */
export async function startTranscribeStream({
  mint,
  stream: externalStream,
  onReady,
  onPartial,
  onCommit,
  onError,
  onClose,
} = {}) {
  if (typeof mint !== 'function') {
    throw new Error('startTranscribeStream: `mint` function is required');
  }

  const fail = (err) => { if (onError) onError(err); };

  // ── 4.1 mint (must be inside the user gesture) ──────────────────────────
  const minted = await mint();
  if (!minted || !minted.url) throw new Error('Mint returned no stream URL');

  // ── 4.2 microphone ──────────────────────────────────────────────────────
  // An existing MediaStream may be passed in (e.g. the one MediaRecorder is
  // already holding). Opening a SECOND getUserMedia capture on iOS is not
  // reliable, so callers that already have a mic stream must share it. When
  // shared, the caller owns teardown of the tracks; we never stop them.
  // Deliberately NOT requesting sampleRate when we do open one: browsers that
  // cannot honour it behave inconsistently, and we resample unconditionally.
  const ownsStream = !externalStream;
  const stream = externalStream || await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
  });

  // ── 4.3 audio graph ─────────────────────────────────────────────────────
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  let ctx;
  try {
    ctx = new AudioCtx({ sampleRate: TARGET_SAMPLE_RATE });
  } catch {
    ctx = new AudioCtx();
  }
  // iOS requires resume() inside the gesture that started this call.
  if (ctx.state === 'suspended') { try { await ctx.resume(); } catch { /* noop */ } }

  const source = ctx.createMediaStreamSource(stream);
  const ratio = ctx.sampleRate / TARGET_SAMPLE_RATE;

  let workletLoaded = false;
  let node = null;
  let blobUrl = null;

  const pending = [];
  let ws = null;
  let stopped = false;

  const sendPCM = (int16Buffer) => {
    if (stopped) return;
    const payload = new Uint8Array(int16Buffer);
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(buildAudioEvent(payload));
    } else if (pending.length < 100) {
      pending.push(payload); // ~10 s of audio while the socket opens
    }
  };

  if (ctx.audioWorklet) {
    try {
      const blob = new Blob([WORKLET_SRC], { type: 'application/javascript' });
      blobUrl = URL.createObjectURL(blob);
      await ctx.audioWorklet.addModule(blobUrl);
      node = new AudioWorkletNode(ctx, 'orella-pcm', {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        processorOptions: { targetRate: TARGET_SAMPLE_RATE, frameSamples: FRAME_SAMPLES },
      });
      node.port.onmessage = (e) => sendPCM(e.data);
      workletLoaded = true;
    } catch (err) {
      // R2: AudioWorklet blocked (CSP / capacitor:// origin). Fall through.
      workletLoaded = false;
      node = null;
      if (blobUrl) { URL.revokeObjectURL(blobUrl); blobUrl = null; }
    }
  }

  let processor = null;
  if (!workletLoaded) {
    if (!ctx.createScriptProcessor) {
      if (ownsStream) stream.getTracks().forEach((t) => t.stop());
      await ctx.close();
      throw new Error('No AudioWorklet and no ScriptProcessorNode — streaming unavailable');
    }
    const rsState = { pos: 0 };
    processor = ctx.createScriptProcessor(4096, 1, 1);
    processor.onaudioprocess = (e) => {
      const pcm = floatToPCM16(e.inputBuffer.getChannelData(0), ratio, rsState);
      if (pcm.length) sendPCM(pcm.buffer);
    };
    // ScriptProcessorNode only fires when connected to a destination.
    const sink = ctx.createGain();
    sink.gain.value = 0;
    processor.connect(sink);
    sink.connect(ctx.destination);
    node = processor;
  }

  source.connect(node);

  // ── 4.4 socket ──────────────────────────────────────────────────────────
  ws = new WebSocket(minted.url);
  ws.binaryType = 'arraybuffer';

  const info = {
    contextSampleRate: ctx.sampleRate,
    targetSampleRate: TARGET_SAMPLE_RATE,
    resampling: ctx.sampleRate !== TARGET_SAMPLE_RATE,
    workletLoaded,
    engine: minted.engine,
    languageCode: minted.languageCode,
    languageUnverified: !!minted.languageUnverified,
    speakerLabels: !!minted.speakerLabels,
    specialty: minted.specialty || null,
  };

  await new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Transcribe handshake timed out')), 10000);
    ws.onopen = () => {
      clearTimeout(timer);
      while (pending.length) ws.send(buildAudioEvent(pending.shift()));
      if (onReady) onReady(info);
      resolve();
    };
    ws.onerror = () => {
      clearTimeout(timer);
      // A rejected language code closes here. Callers route to the batch leg;
      // never silently retry in English.
      reject(new Error('Transcribe handshake failed'));
    };
  }).catch(async (err) => {
    stopped = true;
    try { source.disconnect(); node.disconnect(); } catch { /* noop */ }
    if (ownsStream) stream.getTracks().forEach((t) => t.stop());
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    await ctx.close().catch(() => {});
    throw err;
  });

  ws.onmessage = (event) => {
    let msg;
    try {
      msg = decodeMessage(event.data);
    } catch (err) {
      fail(new Error('Malformed event-stream frame: ' + err.message));
      return;
    }

    const type = msg.headers[':message-type'];

    if (type === 'exception') {
      let detail = msg.payload;
      try { detail = JSON.parse(msg.payload).Message || detail; } catch { /* noop */ }
      const err = new Error(detail);
      err.name = msg.headers[':exception-type'] || 'TranscribeException';
      fail(err);
      return;
    }

    if (type !== 'event') return;

    let body;
    try { body = JSON.parse(msg.payload); } catch { return; }

    const results = body?.Transcript?.Results || [];
    for (const r of results) {
      const alt = r.Alternatives && r.Alternatives[0];
      if (!alt) continue;
      const text = alt.Transcript || '';
      if (!text) continue;
      if (r.IsPartial) {
        if (onPartial) onPartial(text, r);
      } else if (onCommit) {
        onCommit(text, r);
      }
    }
  };

  ws.onclose = (e) => {
    if (onClose) onClose({ code: e.code, reason: e.reason, clean: e.wasClean });
  };

  // ── 4.5 teardown ────────────────────────────────────────────────────────
  /**
   * stop({ deliver }) — end the stream.
   *
   * deliver: true (default) is the END OF A RECORDING. The trailing flush is
   *   the patient's or doctor's last sentence, so the promise waits for it.
   *
   * deliver: false is a DISCARD — the caller has thrown the recording away
   *   (a Clear button, an unmounting screen) and does not want the text. It
   *   detaches the message handler first, so no late commit can write into a
   *   state the UI has already reset, and it does not wait.
   *
   * WHY THE SECOND MODE EXISTS. Waiting for the flush was the fix for
   * FU-STREAM-DRAIN-1, but it widened an existing discard race from ~800 ms to
   * FLUSH_TIMEOUT_MS: useConsultRecording.clear() calls stop() WITHOUT awaiting
   * it and then blanks committedRef, so a flush landing afterwards resurrects
   * text into a cleared editor. Making the drain longer made that likelier, so
   * the discard path now says what it means instead of racing a timer.
   */
  const stop = async ({ deliver = true } = {}) => {
    if (stopped) return;
    stopped = true;

    // Detached BEFORE anything else on the discard path: the whole point is
    // that no further onPartial/onCommit reaches a caller that has moved on.
    if (!deliver && ws) ws.onmessage = null;

    try { source.disconnect(); } catch { /* noop */ }
    try { node.disconnect(); } catch { /* noop */ }
    if (processor) processor.onaudioprocess = null;
    else if (node && node.port) node.port.onmessage = null;

    if (ownsStream) stream.getTracks().forEach((t) => t.stop());

    // Release the microphone before waiting on the socket. The capture is over
    // either way, and the recording indicator should not stay lit for the
    // length of the drain.
    if (blobUrl) { URL.revokeObjectURL(blobUrl); blobUrl = null; }
    await ctx.close().catch(() => {});

    if (!ws) return;

    if (!deliver || ws.readyState !== WebSocket.OPEN) {
      try { ws.close(1000, 'client stop'); } catch { /* noop */ }
      return;
    }

    // Zero-length AudioEvent signals end-of-stream; Transcribe then flushes
    // whatever is left as a final result and closes the socket itself.
    try { ws.send(buildAudioEvent(new Uint8Array(0))); } catch { /* noop */ }

    // ★ DO NOT RESOLVE UNTIL THAT FLUSH HAS LANDED. onCommit fires from
    // ws.onmessage, so a caller that reads its committed text after `await
    // stop()` is reading a value this promise is still responsible for. See
    // FLUSH_TIMEOUT_MS above for what went wrong when it resolved early.
    await new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        // Transcribe never closed. Stop waiting, but close cleanly so the
        // socket does not leak.
        try { ws.close(1000, 'client stop'); } catch { /* noop */ }
        finish();
      }, FLUSH_TIMEOUT_MS);
      // Chained, not replaced: the caller's onClose is a real observer and
      // stealing it here would silently unhook every stream-closed log.
      const chained = ws.onclose;
      ws.onclose = (e) => {
        try { if (chained) chained(e); } finally { finish(); }
      };
    });
  };

  return { stop, info };
}

export const __internals = { crc32, buildAudioEvent, decodeMessage, encodeHeaders };
